import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  FileText, Search, ArrowLeft, Loader2, RefreshCw, Eye, Download,
  XCircle, RotateCcw, CheckCircle, Clock, AlertTriangle, Filter
} from 'lucide-react';

export default function FiscalNotas() {
  const [loading, setLoading] = useState(true);
  const [notas, setNotas] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filtros, setFiltros] = useState({
    status: '',
    competencia_mes: '',
    competencia_ano: String(new Date().getFullYear()),
    search: ''
  });
  const [notaSelecionada, setNotaSelecionada] = useState(null);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [cancelDialogAberto, setCancelDialogAberto] = useState(false);
  const [motivoCancelamento, setMotivoCancelamento] = useState('');
  const [processando, setProcessando] = useState(false);
  
  const navigate = useNavigate();
  const limit = 20;

  useEffect(() => {
    loadNotas();
  }, [page, filtros.status, filtros.competencia_mes, filtros.competencia_ano]);

  const loadNotas = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filtros.status) params.status = filtros.status;
      if (filtros.competencia_mes) params.competencia_mes = parseInt(filtros.competencia_mes);
      if (filtros.competencia_ano) params.competencia_ano = parseInt(filtros.competencia_ano);

      const response = await api.get('/fiscal/notas', { params });
      setNotas(response.data.notas || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Erro ao carregar notas fiscais');
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalhes = async (nota) => {
    try {
      const response = await api.get(`/fiscal/notas/${nota.id}`);
      setNotaSelecionada(response.data);
      setDialogAberto(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes da nota');
    }
  };

  const handleCancelar = async () => {
    if (motivoCancelamento.length < 15) {
      toast.error('O motivo deve ter pelo menos 15 caracteres');
      return;
    }

    setProcessando(true);
    try {
      await api.post(`/fiscal/notas/${notaSelecionada.id}/cancelar?motivo=${encodeURIComponent(motivoCancelamento)}`);
      toast.success('Solicitação de cancelamento registrada');
      setCancelDialogAberto(false);
      setMotivoCancelamento('');
      loadNotas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar nota');
    } finally {
      setProcessando(false);
    }
  };

  const handleReprocessar = async (notaId) => {
    setProcessando(true);
    try {
      await api.post(`/fiscal/notas/${notaId}/reprocessar`);
      toast.success('Nota adicionada à fila de reprocessamento');
      loadNotas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reprocessar nota');
    } finally {
      setProcessando(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      autorizada: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Autorizada' },
      pendente: { bg: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Pendente' },
      processando: { bg: 'bg-blue-100 text-blue-700', icon: Loader2, label: 'Processando' },
      rejeitada: { bg: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejeitada' },
      cancelada: { bg: 'bg-gray-100 text-gray-700', icon: XCircle, label: 'Cancelada' },
      erro: { bg: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'Erro' }
    };
    const config = styles[status] || styles.pendente;
    const Icon = config.icon;
    return (
      <Badge className={`${config.bg} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const meses = [
    { value: '1', label: 'Janeiro' },
    { value: '2', label: 'Fevereiro' },
    { value: '3', label: 'Março' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Maio' },
    { value: '6', label: 'Junho' },
    { value: '7', label: 'Julho' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' }
  ];

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 space-y-6" data-testid="fiscal-notas">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/fiscal')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Notas Fiscais Emitidas
          </h1>
          <p className="text-muted-foreground">Consulte, baixe e gerencie notas fiscais</p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={filtros.status || "_all"} 
                onValueChange={(v) => setFiltros(prev => ({ ...prev, status: v === "_all" ? "" : v }))}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos</SelectItem>
                  <SelectItem value="autorizada">Autorizada</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="processando">Processando</SelectItem>
                  <SelectItem value="rejeitada">Rejeitada</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                  <SelectItem value="erro">Erro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Competência</Label>
              <div className="flex gap-2">
                <Select 
                  value={filtros.competencia_mes || "_all"} 
                  onValueChange={(v) => setFiltros(prev => ({ ...prev, competencia_mes: v === "_all" ? "" : v }))}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Mês" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Todos</SelectItem>
                    {meses.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={filtros.competencia_ano} 
                  onValueChange={(v) => setFiltros(prev => ({ ...prev, competencia_ano: v }))}
                >
                  <SelectTrigger className="w-[100px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026].map(ano => (
                      <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button variant="outline" onClick={loadNotas}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Notas ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : notas.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nenhuma nota fiscal encontrada.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tomador</TableHead>
                    <TableHead>Aluno</TableHead>
                    <TableHead>Competência</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead className="text-right">ISS</TableHead>
                    <TableHead>Data Emissão</TableHead>
                    <TableHead className="text-center">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notas.map(nota => (
                    <TableRow key={nota.id}>
                      <TableCell className="font-mono">
                        {nota.numero || '-'}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(nota.status)}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {nota.tomador_nome || '-'}
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        {nota.aluno_nome || '-'}
                      </TableCell>
                      <TableCell>
                        {nota.competencia_mes?.toString().padStart(2, '0')}/{nota.competencia_ano}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        R$ {(nota.valor_servicos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        R$ {(nota.valor_iss || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>
                        {nota.data_emissao 
                          ? (() => { const m = String(nota.data_emissao).match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : nota.data_emissao; })()
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleVerDetalhes(nota)}
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {nota.pdf_url && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => window.open(nota.pdf_url, '_blank')}
                              title="Baixar PDF"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                          {['erro', 'rejeitada'].includes(nota.status) && (
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleReprocessar(nota.id)}
                              disabled={processando}
                              title="Reprocessar"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Anterior
              </Button>
              <span className="flex items-center px-4 text-sm text-muted-foreground">
                Página {page} de {totalPages}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de detalhes */}
      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Detalhes da Nota Fiscal
            </DialogTitle>
          </DialogHeader>

          {notaSelecionada && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Número</Label>
                  <p className="font-mono font-medium">{notaSelecionada.numero || 'Pendente'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">{getStatusBadge(notaSelecionada.status)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Código de Verificação</Label>
                  <p className="font-mono text-sm">{notaSelecionada.codigo_verificacao || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Data de Emissão</Label>
                  <p>{notaSelecionada.data_emissao 
                    ? new Date(notaSelecionada.data_emissao).toLocaleString('pt-BR')
                    : 'Pendente'
                  }</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Tomador</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Nome</Label>
                    <p>{notaSelecionada.tomador_nome}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">CPF/CNPJ</Label>
                    <p className="font-mono">{notaSelecionada.tomador_cpf_cnpj}</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-2">Valores</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Valor dos Serviços</Label>
                    <p className="font-medium text-lg">
                      R$ {(notaSelecionada.valor_servicos || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Alíquota ISS</Label>
                    <p>{notaSelecionada.aliquota_iss}%</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Valor ISS</Label>
                    <p className="text-purple-600 font-medium">
                      R$ {(notaSelecionada.valor_iss || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-muted-foreground">Discriminação do Serviço</Label>
                <p className="text-sm mt-1">{notaSelecionada.discriminacao}</p>
              </div>

              {notaSelecionada.erro_mensagem && (
                <div className="p-4 bg-red-50 rounded-lg">
                  <Label className="text-red-700">Mensagem de Erro</Label>
                  <p className="text-sm text-red-600 mt-1">{notaSelecionada.erro_mensagem}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {notaSelecionada?.status === 'autorizada' && (
              <Button 
                variant="destructive" 
                onClick={() => {
                  setDialogAberto(false);
                  setCancelDialogAberto(true);
                }}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Cancelar Nota
              </Button>
            )}
            {notaSelecionada?.pdf_url && (
              <Button onClick={() => window.open(notaSelecionada.pdf_url, '_blank')}>
                <Download className="w-4 h-4 mr-2" />
                Baixar PDF
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogAberto(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de cancelamento */}
      <Dialog open={cancelDialogAberto} onOpenChange={setCancelDialogAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Cancelar Nota Fiscal
            </DialogTitle>
            <DialogDescription>
              Informe o motivo do cancelamento. Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo do Cancelamento *</Label>
              <Textarea
                id="motivo"
                placeholder="Descreva detalhadamente o motivo do cancelamento (mínimo 15 caracteres)"
                value={motivoCancelamento}
                onChange={(e) => setMotivoCancelamento(e.target.value)}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {motivoCancelamento.length}/15 caracteres mínimos
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogAberto(false)}>
              Voltar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelar}
              disabled={processando || motivoCancelamento.length < 15}
            >
              {processando ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelando...
                </>
              ) : (
                'Confirmar Cancelamento'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
